import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';
import { FormField } from '../../../components/Shared/FormField';
import { Plus, Trash2 } from 'lucide-react';
import type { MaterialItem } from '../../../types';

interface MaterialManagerProps {
  materials: MaterialItem[];
  onChange: (materials: MaterialItem[]) => void;
  errors?: Record<string, string>;
}

export function MaterialManager({ materials, onChange, errors }: MaterialManagerProps) {
  const { t } = useTranslation(['tasks']);
  
  const addMaterial = () => {
    onChange([...materials, { name: '', quantity: '' }]);
  };

  const removeMaterial = (index: number) => {
    onChange(materials.filter((_, i) => i !== index));
  };

  const updateMaterial = (index: number, field: 'name' | 'quantity', value: string) => {
    const updated = materials.map((mat, i) =>
      i === index ? { ...mat, [field]: value || '' } : mat
    );
    onChange(updated);
  };

  return (
    <FormField label={t('tasks:materials.title')}>
      <div className="space-y-3">
        {materials.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            {t('tasks:materials.empty')}
          </div>
        ) : (
          materials.map((material, index) => (
            <div key={index} className="flex items-start gap-2">
              <div className="flex-1 grid grid-cols-2 gap-2">
                <div>
                  <Input
                    placeholder={t('tasks:materials.namePlaceholder')}
                    value={material.name || ''}
                    onChange={(e) => updateMaterial(index, 'name', e.target.value)}
                    className={errors?.[`material_${index}`] ? 'border-red-500' : ''}
                  />
                  {errors?.[`material_${index}`] && (
                    <p className="mt-1 text-sm text-red-500">{errors[`material_${index}`]}</p>
                  )}
                </div>
                <Input
                  placeholder={t('tasks:materials.quantityPlaceholder')}
                  value={material.quantity || ''}
                  onChange={(e) => updateMaterial(index, 'quantity', e.target.value)}
                />
              </div>
              <Button
                type="button"
                variant="danger"
                size="sm"
                onClick={() => removeMaterial(index)}
                className="mt-0.5"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))
        )}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={addMaterial}
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('tasks:materials.addMaterial')}
        </Button>
      </div>
    </FormField>
  );
}
